import type { MinPriceDays } from '$saga/views/Tour/VisibleDates/types';
import type { DateValue } from '@internationalized/date';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
require('dayjs/locale/fr');
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.locale('fr');

export interface DateRange {
    startDate: Dayjs;
    endDate: Dayjs;
}

// tour date format is DD/MM/YYYY
export const formatTourDate = (date: string) => {
    return dayjs(date).format('DD MMM');
};

export const formatTourDate2 = (date: string) => {
    return dayjs(date).format('DD MMM YYYY');
};

/**
 * Return the first available month index (dayjs index starts at 0)
 * @param unavailableDates
 * @returns dayjs Date
 */
export const findFirstAvailableMonth = (unavailableDates: DateRange[]) => {
    // Sort the unavailable periods by start date
    unavailableDates.sort((a, b) =>
        a.startDate.isBefore(b.startDate) ? -1 : 1,
    );

    // Start from the current date
    let currentDate = dayjs(); // Set to the first day of the current month

    for (const period of unavailableDates) {
        if (currentDate.isBefore(period.startDate)) {
            return currentDate;
        }

        // if our current date is within the unavailable period, we move to the end of the period + 1 day
        if (currentDate.isSameOrBefore(period.endDate)) {
            currentDate = period.endDate.add(1, 'day');
        }
    }

    return currentDate;
};

/**
 * Check if a date should be disabled based on closed sales periods and price data availability
 * @param date - The date to check
 * @param closedSales - Array of closed sales periods
 * @param priceData - Price data object (minPricePerDays or cheapestDailyPrices)
 * @returns true if the date should be disabled
 */
export const isDateDisabled = (
    date: DateValue,
    closedSales: DateRange[],
    priceData: MinPriceDays,
) => {
    let isDisabled = false;
    const d = dayjs(date.toString());

    // Check closed sales periods
    for (const closedDate of closedSales) {
        // '[]' is inclusive start+end day dates
        if (d.isBetween(closedDate.startDate, closedDate.endDate, 'day', '[]'))
            isDisabled = true;
    }

    // Also disable dates beyond the price data window (no price data)
    const dateKey = d.format('YYYY-MM-DD');
    const priceDay = priceData[dateKey];
    if (!priceDay) {
        isDisabled = true;
    }

    return isDisabled;
};
